// Logical-view helpers shared by the diagram and the diff.
import type { EntitySpec, ModelSpec, OwnedSpec, RelationshipSpec } from './schema';

/**
 * A join entity with no data of its own: exactly two one-to-many foreign keys that together form
 * its key and are its only properties. Views draw it as a many-to-many link.
 */
export function isPureJoin(model: ModelSpec, key: string): [RelationshipSpec, RelationshipSpec] | null {
  const e = model.entities[key];
  if (!e || e.base || e.owned || e.complex || !e.key) return null;
  if (Object.values(model.entities).some((x) => x.base === key)) return null;
  const rels = model.relationships.filter((r) => r.dependent === key);
  if (rels.length !== 2 || rels.some((r) => r.cardinality !== 'one-to-many')) return null;
  const fkProps = new Set(rels.flatMap((r) => r.foreignKey));
  const props = Object.keys(e.properties);
  if (props.length !== fkProps.size || props.some((p) => !fkProps.has(p))) return null;
  if (e.key.length !== fkProps.size || e.key.some((k) => !fkProps.has(k))) return null;
  return [rels[0]!, rels[1]!];
}

/** Primary key of an entity, following base types. */
export function primaryKeyOf(model: ModelSpec, key: string): string[] {
  const seen = new Set<string>();
  let current: string | undefined = key;
  while (current && !seen.has(current)) {
    seen.add(current);
    const e: EntitySpec | undefined = model.entities[current];
    if (!e) break;
    if (e.key) return e.key;
    current = e.base;
  }
  return [];
}

/** Resolves an entity key or owned path (`Order.Shipping`) to its specification. */
export function specAt(model: ModelSpec, path: string): EntitySpec | OwnedSpec | undefined {
  if (model.entities[path]) return model.entities[path];
  const parts = path.split('.');
  for (let i = parts.length - 1; i >= 1; i--) {
    const root = model.entities[parts.slice(0, i).join('.')];
    if (!root) continue;
    let spec: EntitySpec | OwnedSpec | undefined = root;
    for (const nav of parts.slice(i)) spec = spec?.owned?.[nav];
    if (spec) return spec;
  }
  return undefined;
}

/** Splits an owned path into its root entity key and navigation path. */
export function rootOfPath(model: ModelSpec, path: string): { root: string; rest: string[] } {
  if (model.entities[path]) return { root: path, rest: [] };
  const parts = path.split('.');
  for (let i = parts.length - 1; i >= 1; i--) {
    const root = parts.slice(0, i).join('.');
    if (model.entities[root]) return { root, rest: parts.slice(i) };
  }
  return { root: path, rest: [] };
}

export const emptyModel = (): ModelSpec => ({ entities: {}, relationships: [] });
